import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  saveXdrGroupSettings,
  saveXdrSettings,
  xdrGroupSettingsKey,
  xdrGroupSettingsQuery,
  xdrSettingsQuery,
  XDR_GROUP_SETTINGS_KEY,
  XDR_LIMIT_KEYS,
  XDR_SETTINGS_KEY,
  type XdrChannel,
  type XdrGroupOption,
  type XdrGroupSave,
  type XdrLimitKey,
  type XdrSettingsPage,
  type XdrSettingsSave,
} from '../../api/securiteeXdrSettings'
import { Spinner } from '../../ui/Spinner'
import { SiemGroupsSection } from './SiemGroupsSection'
import { XdrPolicySections } from './XdrPolicySections'
import { XdrSection } from './XdrSection'

/**
 * Settings > Securitee > XDR - XDR si ce colecteaza agentul (canalele Event
 * Log cu ID-urile lor, limitele), pe toata firma sau pe un GRUP de aparate.
 * Ecran nou, fara pereche in portalul vechi; fila apare numai cu dreptul
 * `securitee.xdr_settings` (read vede, edit salveaza).
 *
 * Pana pe 6 octombrie 2026 era ecran separat, Securitee > XDR settings
 * (`/securitee/xdr_settings`, care acum duce aici). E acelasi panou, pus ca
 * fila - nu o copie.
 *
 * Selectorul de sus alege "Whole company" sau un grup. Intr-un grup fiecare
 * parte (XDR pornit/oprit, canalele, fiecare limita) ori mosteneste firma
 * (bifa "Inherit from company", trimisa ca null), ori are valoarea ei. Agentul
 * ia, pe fiecare camp: aparatul (bifa din Modules) > grupul > firma.
 * Conexiunea Wazuh e a platformei: se arata numai in "Whole company".
 *
 * Validarea e a serverului: ce nu e numar se trimite asa cum e scris, iar
 * mesajul care se intoarce (422) se arata langa campul lui.
 *
 * Toate setarile XDR stau aici, in sectiuni care se strang: colectarea (deschisa),
 * regulile firmei (`XdrPolicySections`) si conexiunea Wazuh.
 */

export const LIMIT_LABELS: Record<XdrLimitKey, string> = {
  rate_per_minute: 'Events per minute',
  queue_size: 'Queue size',
  process_interval: 'Process snapshot interval',
  net_interval: 'Network snapshot interval',
}

const LIMIT_UNITS: Record<XdrLimitKey, string> = {
  rate_per_minute: 'events',
  queue_size: 'events',
  process_interval: 'seconds',
  net_interval: 'seconds',
}

/* nu `.checkbox` de Bootstrap: foaia aplicatiei ii scoate marginea negativa, si bifa cadea peste text */
const CHECK_LABEL = { display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 0 } as const
const CHECK_INPUT = { position: 'static', margin: 0 } as const

interface ChannelDraft {
  name: string
  on: boolean
  ids: string
  defaultIds: number[]
  custom: boolean
}

interface Inherit {
  enabled: boolean
  channels: boolean
  limits: Record<XdrLimitKey, boolean>
}

interface Draft {
  enabled: boolean
  channels: ChannelDraft[]
  limits: Record<XdrLimitKey, string>
  /** numai intr-un grup; pe firma totul e false */
  inherit: Inherit
}

interface FieldError {
  field: string
  channel: string
  message: string
}

function idsText(ids: number[]): string {
  return ids.join(', ')
}

function channelDrafts(channels: XdrChannel[]): ChannelDraft[] {
  return channels.map((c) => ({ name: c.name, on: c.on, ids: idsText(c.ids), defaultIds: c.default_ids, custom: c.custom }))
}

function limitText(value: number | null): string {
  return value === null ? '' : String(value)
}

function draftOf(page: XdrSettingsPage): Draft {
  const limits = {} as Record<XdrLimitKey, string>
  const inheritLimits = {} as Record<XdrLimitKey, boolean>
  for (const key of XDR_LIMIT_KEYS) {
    limits[key] = limitText(page.limits[key])
    inheritLimits[key] = page.inherited?.limits[key] ?? false
  }
  return {
    enabled: page.enabled,
    channels: channelDrafts(page.channels),
    limits,
    inherit: { enabled: page.inherited?.enabled ?? false, channels: page.inherited?.channels ?? false, limits: inheritLimits },
  }
}

/** "7045, 104;4697" -> [7045, 104, 4697]; ce nu e numar ramane text, ca serverul sa-l refuze cu mesajul lui. */
function parseIds(text: string): Array<number | string> {
  return text
    .split(/[\s,;]+/)
    .filter((t) => t !== '')
    .map((t) => (/^\d+$/.test(t) ? Number(t) : t))
}

/** Acelasi sir de ID-uri, oricum ar fi despartit sau ordonat. */
function sameIds(text: string, ids: number[]): boolean {
  const a = parseIds(text).map(String).sort()
  const b = ids.map(String).sort()
  return a.length === b.length && a.every((v, i) => v === b[i])
}

/**
 * Randul de deasupra tabelului strans: "7 channels, 92 event IDs - default",
 * sau "5 of 7 channels on, 80 event IDs, custom IDs on 2". Se socoteste din
 * ce e in formular, deci urmareste schimbarile nesalvate.
 */
function channelsSummary(channels: ChannelDraft[]): string {
  const on = channels.filter((c) => c.on)
  const ids = on.reduce((n, c) => n + parseIds(c.ids).length, 0)
  const custom = on.filter((c) => c.custom || !sameIds(c.ids, c.defaultIds)).length
  const head = on.length === channels.length ? `${channels.length} channels` : `${on.length} of ${channels.length} channels on`
  const tail = custom > 0 ? `, custom IDs on ${custom}` : on.length === channels.length ? ' - default' : ''
  return `${head}, ${ids} event IDs${tail}`
}

function limitValue(text: string): number | string | null {
  const v = text.trim()
  return v === '' ? null : /^\d+$/.test(v) ? Number(v) : v
}

function channelsBody(draft: Draft): XdrSettingsSave['channels'] {
  return draft.channels.map((c) => ({ name: c.name, on: c.on, ids: parseIds(c.ids) }))
}

function bodyOf(draft: Draft): XdrSettingsSave {
  const limits = {} as XdrSettingsSave['limits']
  for (const key of XDR_LIMIT_KEYS) limits[key] = limitValue(draft.limits[key])
  return { enabled: draft.enabled, channels: channelsBody(draft), limits }
}

/** Corpul unui grup: o parte mostenita pleaca null. */
function groupBodyOf(draft: Draft): XdrGroupSave {
  const limits = {} as XdrGroupSave['limits']
  for (const key of XDR_LIMIT_KEYS) limits[key] = draft.inherit.limits[key] ? null : limitValue(draft.limits[key])
  return {
    enabled: draft.inherit.enabled ? null : draft.enabled,
    channels: draft.inherit.channels ? null : channelsBody(draft),
    limits,
  }
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`
}

function groupLabel(g: XdrGroupOption): string {
  return `${g.name} (${plural(g.devices, 'device')})${g.customized ? ' - customized' : ''}`
}

export function XdrPanel() {
  const client = useQueryClient()
  const page = useQuery(xdrSettingsQuery())
  // null = toata firma; altfel grupul ales
  const [groupId, setGroupId] = useState<number | null>(null)
  const group = useQuery({ ...xdrGroupSettingsQuery(groupId ?? 0), enabled: groupId !== null })
  // cheia formularului: se reface din raspuns dupa fiecare salvare reusita
  const [version, setVersion] = useState(0)
  const [saved, setSaved] = useState(false)

  /* Conexiunea Wazuh e a platformei, nu a firmei: se arata si fara firma in
     antet (409) si fara dreptul XDR (403), cand serverul spune `wazuh_admin`. */
  const wazuhAdmin = page.data?.wazuh_admin === true || (page.error instanceof ApiError && page.error.details?.wazuh_admin === true)

  const groups = page.data?.groups ?? []
  const current = groupId === null ? page : group

  const onSaved = (next: XdrSettingsPage) => {
    if (groupId === null) {
      client.setQueryData(XDR_SETTINGS_KEY, next)
      // ce mostenesc grupurile s-a schimbat
      void client.invalidateQueries({ queryKey: XDR_GROUP_SETTINGS_KEY })
    } else {
      client.setQueryData(xdrGroupSettingsKey(groupId), next)
      // numaratorile si semnul "customized" din selector
      void client.invalidateQueries({ queryKey: XDR_SETTINGS_KEY, exact: true })
    }
    setSaved(true)
    setVersion((v) => v + 1)
  }

  let body
  if (page.error instanceof ApiError && page.error.status === 403 && wazuhAdmin) {
    body = null
  } else if (page.error instanceof ApiError && page.error.status === 409) {
    body = <div className="alert alert-warning xdr-company">{page.error.message}</div>
  } else if (page.error instanceof ApiError && page.error.status === 403) {
    body = (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  } else if (current.error instanceof ApiError && current.error.status === 404) {
    body = <div className="alert alert-warning xdr-group-missing">{current.error.message}</div>
  } else if (current.error) {
    body = <div className="alert alert-danger">Something went wrong: {(current.error as Error).message}</div>
  } else if (!current.data) {
    body = <Spinner />
  } else {
    body = (
      <XdrSettingsForm
        key={`${groupId ?? 'company'}-${version}`}
        page={current.data}
        groupId={groupId}
        saved={saved}
        onChange={() => setSaved(false)}
        onSaved={onSaved}
      />
    )
  }

  const collection = page.data
    ? `XDR ${page.data.enabled ? 'on' : 'off'} for the company, ${page.data.devices.xdr_on} of ${plural(page.data.devices.total, 'device')} collecting`
    : undefined

  return (
    <div id="content" className="xdr-settings">
      <header className="panel-heading">
        <h3>
          <strong>XDR</strong> settings
        </h3>
        <label className="color">everything XDR does for this company: what the agent collects, incidents, tickets and response</label>
      </header>
      <XdrSection id="collection" title="Collection on the devices" summary={collection} defaultOpen>
        {page.data && groups.length > 0 && (
          <div className="form-group xdr-scope" style={{ maxWidth: 420 }}>
            <label htmlFor="xdr-scope">Scope</label>
            <select
              id="xdr-scope"
              className="form-control"
              value={groupId === null ? '' : String(groupId)}
              onChange={(e) => {
                setGroupId(e.target.value === '' ? null : Number(e.target.value))
                setSaved(false)
              }}
            >
              <option value="">Whole company</option>
              {groups.map((g) => (
                <option key={g.id} value={String(g.id)}>
                  {groupLabel(g)}
                </option>
              ))}
            </select>
            <span className="help-block">Each group follows the company, unless you give it its own settings here.</span>
          </div>
        )}
        {body}
      </XdrSection>
      {page.data && <XdrPolicySections />}
      {wazuhAdmin && groupId === null && (
        <XdrSection id="siem-groups" title="SIEM groups" summary="The Wazuh connection of each group of companies" defaultOpen={!page.data}>
          <SiemGroupsSection />
        </XdrSection>
      )}
    </div>
  )
}

const INFO = {
  company:
    "XDR on the company's devices. Applies to every device whose group inherits from the company, and to devices with no group. A group with its own setting overrides it; a device set to On or Off in Modules keeps its own setting.",
  inherit: "Inherit from company. Ticked: this group's devices follow the company's XDR on/off. Unticked: the group decides on its own, with the checkbox below.",
  group:
    'XDR on the devices of this group. Ticked: every device in this group collects XDR events. Unticked: XDR is off for the whole group, even if the company has it on. A device set to On or Off in Modules keeps its own setting.',
}

/** "i"-ul unei bife: textul la hover si pentru cititorul de ecran. */
function XdrInfo({ which }: { which: keyof typeof INFO }) {
  return <i className="fas fa-info-circle xdr-i" data-for={which} title={INFO[which]} role="img" aria-label={INFO[which]} style={{ marginLeft: 6, color: '#8a99a8' }}></i>
}

/** Bifa "Inherit from company" a unei parti, cu valoarea firmei langa ea. */
function InheritBox({ part, checked, disabled, hint, onChange, info }: { part: string; checked: boolean; disabled: boolean; hint: string; onChange: (on: boolean) => void; info?: keyof typeof INFO }) {
  return (
    <span className="xdr-inherit" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, fontSize: 12, marginBottom: 4 }}>
      {/* marimea pusa pe eticheta: foaia aplicatiei mareste `.form-group label`, si bifa din limite iesea altfel decat celelalte */}
      <label style={{ ...CHECK_LABEL, fontWeight: 'normal', fontSize: 12 }}>
        <input type="checkbox" style={CHECK_INPUT} checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} aria-label={`Inherit ${part} from the company`} />
        Inherit from company
      </label>
      {info && <XdrInfo which={info} />}
      <span className="text-muted">({hint})</span>
    </span>
  )
}

function XdrSettingsForm({
  page,
  groupId,
  saved,
  onChange,
  onSaved,
}: {
  page: XdrSettingsPage
  groupId: number | null
  saved: boolean
  onChange: () => void
  onSaved: (next: XdrSettingsPage) => void
}) {
  const [initial] = useState(() => draftOf(page))
  const [draft, setDraft] = useState(initial)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<FieldError | null>(null)
  // tabelul canalelor sta strans pana il cere omul (sau pana pica un canal la salvare)
  const [channelsOpen, setChannelsOpen] = useState(false)
  const canEdit = page.can_edit
  const inGroup = groupId !== null
  const company = page.company_settings
  const bodyFor = (d: Draft) => JSON.stringify(inGroup ? groupBodyOf(d) : bodyOf(d))
  const dirty = bodyFor(draft) !== bodyFor(initial)
  const channelsLocked = inGroup && draft.inherit.channels

  const update = (next: Draft) => {
    setDraft(next)
    setError(null)
    onChange()
  }
  const setChannel = (i: number, patch: Partial<ChannelDraft>) =>
    update({ ...draft, channels: draft.channels.map((c, j) => (j === i ? { ...c, ...patch } : c)) })

  /* Bifat "Inherit": partea arata iar valoarea firmei. Debifat: porneste de la ce se vede, de modificat. */
  const inheritEnabled = (on: boolean) =>
    update({ ...draft, enabled: on && company ? company.enabled : draft.enabled, inherit: { ...draft.inherit, enabled: on } })
  const inheritChannels = (on: boolean) =>
    update({ ...draft, channels: on && company ? channelDrafts(company.channels) : draft.channels, inherit: { ...draft.inherit, channels: on } })
  const inheritLimit = (key: XdrLimitKey, on: boolean) => {
    const companyValue = company?.limits[key] ?? null
    const value = on ? limitText(companyValue) : draft.limits[key] !== '' ? draft.limits[key] : String(companyValue ?? page.defaults[key])
    update({ ...draft, limits: { ...draft.limits, [key]: value }, inherit: { ...draft.inherit, limits: { ...draft.inherit.limits, [key]: on } } })
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!canEdit || !dirty || busy) return
    setBusy(true)
    setError(null)
    try {
      onSaved(groupId === null ? await saveXdrSettings(bodyOf(draft)) : await saveXdrGroupSettings(groupId, groupBodyOf(draft)))
    } catch (err) {
      const details = err instanceof ApiError ? err.details : undefined
      const field = typeof details?.field === 'string' ? details.field : ''
      setError({
        field,
        channel: typeof details?.channel === 'string' ? details.channel : '',
        message: err instanceof Error ? err.message : 'Something went wrong.',
      })
      if (field === 'channels') setChannelsOpen(true)
    } finally {
      setBusy(false)
    }
  }

  const { total, xdr_on: on, own_row: own } = page.devices
  const switchLabel = inGroup ? 'XDR on the devices of this group' : 'XDR on the whole company'
  const companyLimit = (key: XdrLimitKey) => {
    const v = company?.limits[key] ?? null
    return v === null ? `company: default, ${page.defaults[key]}` : `company: ${v}`
  }

  return (
    <form className="xdr-settings-form" noValidate onSubmit={(e) => void submit(e)}>
      {!canEdit && (
        <div className="alert alert-info xdr-readonly">You can view these settings. Changing them needs the XDR settings edit right.</div>
      )}

      <div className={'form-group xdr-switch' + (error?.field === 'enabled' ? ' has-error' : '')}>
        {inGroup && (
          <InheritBox
            part="XDR on/off"
            checked={draft.inherit.enabled}
            disabled={!canEdit || busy}
            hint={`Company: XDR ${company?.enabled ? 'on' : 'off'}`}
            onChange={inheritEnabled}
            info="inherit"
          />
        )}
        <label style={CHECK_LABEL}>
          <input
            type="checkbox"
            style={CHECK_INPUT}
            checked={draft.enabled}
            disabled={!canEdit || busy || (inGroup && draft.inherit.enabled)}
            onChange={(e) => update({ ...draft, enabled: e.target.checked })}
            aria-label={switchLabel}
          />
          <strong>{switchLabel}</strong>
        </label>
        <XdrInfo which={inGroup ? 'group' : 'company'} />
        {inGroup ? (
          <>
            <span className="help-block xdr-devices">
              {plural(total, 'device')} in this group, {on} with XDR on.
            </span>
            {own > 0 && (
              <span className="help-block xdr-devices-own">
                {own === 1 ? '1 device set to On or Off in Modules keeps its' : `${own} devices set to On or Off in Modules keep their`} own setting.
              </span>
            )}
          </>
        ) : (
          <>
            <span className="help-block xdr-devices">
              {total} devices, {on} with XDR on, {own} set to On or Off in Modules.
            </span>
            <span className="help-block">
              A device set to On or Off in its Modules tab keeps its own setting. The channels and limits below apply to every device of the company, except groups that have their own
              settings.
            </span>
          </>
        )}
      </div>

      <h4 className="bordered" style={{ marginTop: 20 }}>
        Event log channels
      </h4>
      {inGroup && (
        <InheritBox
          part="channels"
          checked={draft.inherit.channels}
          disabled={!canEdit || busy}
          hint={company ? `company: ${channelsSummary(channelDrafts(company.channels))}` : 'company settings'}
          onChange={inheritChannels}
        />
      )}
      <div className="xdr-channels-bar" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <span className="xdr-channels-summary">{channelsSummary(draft.channels)}</span>
        <button
          type="button"
          className="btn btn-default btn-xs xdr-channels-toggle"
          aria-expanded={channelsOpen}
          aria-controls="xdr-channels-region"
          onClick={() => setChannelsOpen((open) => !open)}
        >
          <i className={channelsOpen ? 'fas fa-chevron-up' : 'fas fa-chevron-down'} aria-hidden="true"></i>{' '}
          {channelsOpen ? 'Hide' : canEdit && !channelsLocked ? 'Edit channels' : 'Show channels'}
        </button>
      </div>
      <div id="xdr-channels-region" hidden={!channelsOpen}>
        <p className="text-muted" style={{ fontSize: 12 }}>
          The agent collects only the event IDs listed for each channel that is on. At most {page.max_ids_per_channel} event IDs per channel, separated by commas.
        </p>
        <div className="table-responsive">
          <table className="table table-striped xdr-channels">
            <thead>
              <tr>
                <th style={{ width: 50 }}>On</th>
                <th>Channel</th>
                <th>Event IDs</th>
                {canEdit && <th style={{ width: 140 }}></th>}
              </tr>
            </thead>
            <tbody>
              {draft.channels.map((c, i) => {
                const isDefault = !c.custom && c.ids.replace(/\s+/g, '') === idsText(c.defaultIds).replace(/\s+/g, '')
                const locked = !canEdit || busy || channelsLocked
                return (
                  <tr key={c.name} className={error?.channel === c.name ? 'danger' : undefined}>
                    <td>
                      <input type="checkbox" checked={c.on} disabled={locked} onChange={(e) => setChannel(i, { on: e.target.checked })} aria-label={`Collect ${c.name}`} />
                    </td>
                    <td style={{ whiteSpace: 'normal', overflowWrap: 'anywhere', minWidth: 110 }}>
                      {c.name}
                      {c.custom && <span className="label label-default" style={{ marginLeft: 6 }}>custom</span>}
                    </td>
                    <td>
                      <textarea
                        className="form-control input-sm"
                        rows={c.ids.length > 70 ? 3 : 1}
                        style={{ resize: 'vertical', minWidth: 200 }}
                        value={c.ids}
                        disabled={locked || !c.on}
                        onChange={(e) => setChannel(i, { ids: e.target.value })}
                        aria-label={`Event IDs for ${c.name}`}
                      />
                    </td>
                    {canEdit && (
                      <td>
                        {!c.custom && !isDefault && !channelsLocked && (
                          <button
                            type="button"
                            className="btn btn-default btn-xs"
                            disabled={busy}
                            onClick={() => setChannel(i, { ids: idsText(c.defaultIds) })}
                            aria-label={`Reset ${c.name} to default`}
                          >
                            Reset to default
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <h4 className="bordered" style={{ marginTop: 20 }}>
        Limits
      </h4>
      <p className="text-muted" style={{ fontSize: 12 }}>
        {inGroup ? 'Untick Inherit from company to give this group its own value.' : 'Leave a field empty to use the default.'}
      </p>
      <div className="row xdr-limits">
        {XDR_LIMIT_KEYS.map((key) => {
          const inherited = inGroup && draft.inherit.limits[key]
          return (
            <div key={key} className={'col-sm-6 col-md-3 form-group' + (error?.field === key ? ' has-error' : '')}>
              <label htmlFor={`xdr-${key}`}>{LIMIT_LABELS[key]}</label>
              {inGroup && (
                <InheritBox part={LIMIT_LABELS[key]} checked={draft.inherit.limits[key]} disabled={!canEdit || busy} hint={companyLimit(key)} onChange={(v) => inheritLimit(key, v)} />
              )}
              <input
                id={`xdr-${key}`}
                type="number"
                className="form-control"
                min={page.bounds[key].min}
                max={page.bounds[key].max}
                step={1}
                placeholder={String(page.defaults[key])}
                value={draft.limits[key]}
                disabled={!canEdit || busy || inherited}
                onChange={(e) => update({ ...draft, limits: { ...draft.limits, [key]: e.target.value } })}
              />
              <span className="help-block">
                Default {page.defaults[key]} {LIMIT_UNITS[key]}. Between {page.bounds[key].min} and {page.bounds[key].max}.
              </span>
            </div>
          )
        })}
      </div>

      {error && <div className="alert alert-danger xdr-error">{error.message}</div>}
      {saved && !error && (
        <div className="alert alert-success xdr-saved">XDR settings saved. Devices pick them up the next time the agent refreshes its profile.</div>
      )}

      {canEdit && (
        <button type="submit" className="btn btn-primary" disabled={!dirty || busy}>
          Save
        </button>
      )}
      {page.updated_at !== null && (
        <p className="text-muted xdr-updated" style={{ fontSize: 12, marginTop: 10 }}>
          Last changed {page.updated_at}
          {page.updated_by_name !== '' ? ` by ${page.updated_by_name}` : ''}.
        </p>
      )}
    </form>
  )
}
